import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { nutritionAPI } from '../../services/api'
import {
  StarIcon,
  PlusIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { contentLanguage } from '../../i18n/supported'

/**
 * FavoriteMealsWidget Component
 *
 * Displays user's favorite meals in the sidebar with quick "Add" buttons.
 * The button label reflects the actual selected date (e.g., "Add to today"
 * or "Add to Feb 19") so users know which day the meal will be logged to.
 * Supports multi-language translation of meal names.
 */
export default function FavoriteMealsWidget({
  selectedDate,
  onMealAdded = () => {},
  onManageFavorites = () => {}
}) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  // Determine the add-button label based on selectedDate vs today
  const getAddButtonLabel = () => {
    const now = new Date()
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

    if (selectedDate === todayStr) {
      return t('favorites.addToToday', 'Add to today')
    }

    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US'
    const dateObj = new Date(selectedDate + 'T12:00:00')
    const formattedDate = dateObj.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    return t('favorites.addToDate', { date: formattedDate, defaultValue: `Add to ${formattedDate}` })
  }

  const addButtonLabel = getAddButtonLabel()

  // Translation state
  const [translatedNames, setTranslatedNames] = useState({})
  const currentLanguage = contentLanguage(i18n?.language)

  // Fetch favorite meals
  const { data: favoritesData, isLoading, error } = useQuery({
    queryKey: ['favoriteMeals'],
    queryFn: () => nutritionAPI.getFavoriteMeals({ limit: 5 }),
    select: (response) => response.data
  })

  const favorites = favoritesData?.favorite_meals || []

  // Auto-translate when language is not English
  useEffect(() => {
    const translateNames = async () => {
      if (currentLanguage === 'en' || favorites.length === 0) {
        setTranslatedNames({})
        return
      }

      const favoriteIds = favorites.map(f => f.id)
      const idsToTranslate = favoriteIds.filter(id => !translatedNames[id])

      if (idsToTranslate.length === 0) return

      try {
        const response = await nutritionAPI.translateFavoriteMealsBatch(idsToTranslate, currentLanguage)
        setTranslatedNames(prev => ({
          ...prev,
          ...response.data.translations
        }))
      } catch (err) {
        console.error('Failed to translate favorite names:', err)
      }
    }

    translateNames()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- translatedNames would cause infinite loop
  }, [favorites, currentLanguage])

  // Add to today mutation
  const addToTodayMutation = useMutation({
    mutationFn: (favoriteId) => nutritionAPI.addFavoriteToToday(favoriteId, null, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      onMealAdded()
    }
  })

  // Get display name (translated or original)
  const getDisplayName = (favorite) => {
    if (currentLanguage !== 'en' && translatedNames[favorite.id]) {
      return translatedNames[favorite.id].translated_name || favorite.name
    }
    return favorite.name
  }

  // Meal type icon
  const getMealTypeIcon = (mealType) => {
    const icons = {
      breakfast: '🌅',
      lunch: '🌞',
      dinner: '🌙',
      snack: '🍎',
      other: '🍽️'
    }
    return icons[mealType?.toLowerCase()] || '🍽️'
  }

  if (isLoading) {
    return (
      <div className="bg-surface-container rounded-lg shadow p-4">
        <div className="flex items-center space-x-2 mb-3">
          <StarIconSolid className="h-5 w-5 text-yellow-500" />
          <h3 className="font-semibold text-gray-900">{t('favorites.title', 'Favorites')}</h3>
        </div>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-surface-container rounded-lg shadow p-4">
        <div className="flex items-center space-x-2 mb-3">
          <StarIconSolid className="h-5 w-5 text-yellow-500" />
          <h3 className="font-semibold text-gray-900">{t('favorites.title', 'Favorites')}</h3>
        </div>
        <p className="text-sm text-red-500">{t('favorites.loadError', 'Failed to load favorites')}</p>
      </div>
    )
  }

  return (
    <div className="bg-surface-container rounded-lg shadow p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <StarIconSolid className="h-5 w-5 text-yellow-500" />
          <h3 className="font-semibold text-gray-900">{t('favorites.title', 'Favorites')}</h3>
        </div>
        <span className="text-xs text-gray-500">{favorites.length} {t('favorites.saved', 'saved')}</span>
      </div>

      {/* Favorites List */}
      {favorites.length === 0 ? (
        <div className="text-center py-4">
          <StarIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{t('favorites.empty', 'No favorites yet')}</p>
          <p className="text-xs text-gray-400 mt-1">
            {t('favorites.emptyHint', 'Click the star on any meal to save it')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {favorites.map((favorite) => (
            <div
              key={favorite.id}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <span className="text-lg flex-shrink-0">{getMealTypeIcon(favorite.default_meal_type)}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {getDisplayName(favorite)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {Math.round(favorite.total_calories)} cal
                    {favorite.times_used > 0 && (
                      <span className="ml-2">• {favorite.times_used}x {t('favorites.used', 'used')}</span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => addToTodayMutation.mutate(favorite.id)}
                disabled={addToTodayMutation.isPending}
                className="flex-shrink-0 p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors disabled:opacity-50"
                title={addButtonLabel}
              >
                {addToTodayMutation.isPending && addToTodayMutation.variables === favorite.id ? (
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <PlusIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* View All Link */}
      {favorites.length > 0 && (
        <button
          onClick={onManageFavorites}
          className="mt-3 w-full flex items-center justify-center text-sm text-indigo-600 hover:text-indigo-800 py-2"
        >
          {t('favorites.viewAll', 'View all favorites')}
          <ChevronRightIcon className="h-4 w-4 ml-1" />
        </button>
      )}
    </div>
  )
}
